import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import LandingNavbar from '../components/LandingNavbar';

const NAVY   = '#1B2A5E';
const GOLD   = '#C9A84C';
const GOLD_DIM = '#A07C28';

const LEVEL_COLORS = {
  Undergraduate:     { bg: 'rgba(201,168,76,.10)', text: '#A07C28',  border: 'rgba(201,168,76,.3)' },
  Postgraduate:      { bg: 'rgba(27,42,94,.08)',   text: '#1B2A5E',  border: 'rgba(27,42,94,.2)'  },
  'Executive Certificate': { bg: 'rgba(27,42,94,.06)', text: '#2D3F7A', border: 'rgba(27,42,94,.15)' },
  Diploma:           { bg: 'rgba(201,168,76,.08)', text: '#A07C28',  border: 'rgba(201,168,76,.2)' },
};

const PROG_ACCENTS = ['#1B2A5E','#2D3F7A','#C9A84C','#A07C28','#243570'];
const PROG_ICONS   = ['ri-briefcase-4-line','ri-scales-line','ri-global-line','ri-bar-chart-box-line','ri-earth-line'];

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export default function ProgrammesPage() {
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('All');

  useEffect(() => {
    api.get('/academic/programmes/public')
      .then(r => setProgrammes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const levels = ['All', ...new Set(programmes.map(p => p.level).filter(Boolean))];
  const filtered = programmes.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || (p.description||'').toLowerCase().includes(q);
    const matchLevel = levelFilter === 'All' || p.level === levelFilter;
    return matchSearch && matchLevel;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Inter, sans-serif' }}>
      <LandingNavbar />

      {/* Breadcrumb — below fixed navbar */}
      <div style={{ background: '#FFF', borderBottom: '1px solid #C8D0E8', padding: '.75rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '5rem' }}>
        <a href="/" style={{ background: 'none', border: 'none', color: NAVY, cursor: 'pointer', fontSize: '.875rem', display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 500, textDecoration: 'none' }}>
          <i className="ri-home-4-line" /> Home
        </a>
        <span style={{ color: '#C8D0E8' }}>›</span>
        <span style={{ fontWeight: 600, color: '#475569', fontSize: '.875rem' }}>All Programmes</span>
      </div>

      <style>{`
        @media(max-width:680px){
          .pp-container{padding:1.5rem 1rem!important}
          .pp-filter-row{flex-direction:column!important}
          .pp-grid{grid-template-columns:1fr!important}
        }
        @media(max-width:900px){
          .pp-grid{grid-template-columns:repeat(auto-fill,minmax(260px,1fr))!important}
        }
      `}</style>
      <div className="pp-container" style={{ maxWidth: 1100, margin: '0 auto', padding: '3rem 1.5rem' }}>
        {/* Page heading */}
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ fontSize: '.75rem', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '.12em', marginBottom: '.5rem' }}>HomeUni · Academic Programmes</div>
          <h1 style={{ fontSize: 'clamp(1.75rem,4vw,2.5rem)', fontWeight: 800, color: NAVY, margin: 0 }}>Find your programme</h1>
          <p style={{ color: '#475569', marginTop: '.5rem', fontSize: '1rem' }}>Accredited degrees and diplomas, fully online, at your own pace.</p>
        </div>

        {/* Search + filter */}
        <div className="pp-filter-row" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <i className="ri-search-line" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search programmes..."
              style={{ width: '100%', paddingLeft: '2.5rem', padding: '.7rem .75rem .7rem 2.5rem', border: '1px solid #C8D0E8', borderRadius: 10, fontSize: '.9rem', outline: 'none', background: '#FFF', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            {levels.map(l => (
              <button
                key={l}
                onClick={() => setLevelFilter(l)}
                style={{ padding: '.5rem 1rem', borderRadius: 8, border: `1px solid ${levelFilter === l ? GOLD : '#C8D0E8'}`, background: levelFilter === l ? GOLD : '#FFF', color: levelFilter === l ? '#FFF' : '#475569', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}
              >{l}</button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94A3B8' }}>Loading programmes...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94A3B8' }}>No programmes match your search.</div>
        ) : (
          <div className="pp-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {filtered.map((prog, i) => {
              const accent = PROG_ACCENTS[i % PROG_ACCENTS.length];
              const icon = PROG_ICONS[i % PROG_ICONS.length];
              const lvl = LEVEL_COLORS[prog.level] || LEVEL_COLORS.Undergraduate;
              return (
                <div
                  key={prog.programme_id}
                  onClick={() => navigate(`/programmes/${slugify(prog.name)}`)}
                  style={{ background: '#FFF', border: '1px solid #C8D0E8', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'all 220ms ease', boxShadow: '0 1px 4px rgba(27,42,94,.04)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(27,42,94,.10)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 4px rgba(27,42,94,.04)'; e.currentTarget.style.borderColor = '#C8D0E8'; }}
                >
                  <div style={{ height: 6, background: accent }} />
                  <div style={{ padding: '1.5rem 1.5rem 0', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={icon} style={{ fontSize: '1.4rem', color: accent }} />
                    </div>
                    <div>
                      <span style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: accent }}>{prog.code}</span>
                      {prog.level && (
                        <span style={{ marginLeft: '.5rem', fontSize: '.7rem', fontWeight: 600, padding: '.2rem .5rem', borderRadius: 5, background: lvl.bg, color: lvl.text, border: `1px solid ${lvl.border}` }}>{prog.level}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: '1rem 1.5rem 1.5rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: '.5rem', lineHeight: 1.4 }}>{prog.name}</div>
                    <div style={{ fontSize: '.82rem', color: '#64748B', marginBottom: '1rem', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {prog.description}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '.78rem', color: '#94A3B8', marginBottom: '1.25rem' }}>
                      <span><i className="ri-time-line" style={{ marginRight: '.25rem' }} />{prog.duration}</span>
                      <span><i className="ri-book-open-line" style={{ marginRight: '.25rem' }} />{prog.course_count || 0} courses</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '1rem', borderTop: '1px solid #EEF0F8' }}>
                      <div style={{ fontWeight: 800, fontSize: '1.1rem', color: GOLD }}>
                        GHS {Number(prog.price || 0).toLocaleString()} <span style={{ fontSize: '.72rem', fontWeight: 400, color: '#94A3B8' }}>/ semester</span>
                      </div>
                      <button
                        style={{ padding: '.5rem 1.1rem', borderRadius: 8, background: NAVY, color: '#FFF', border: 'none', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); navigate(`/programmes/${slugify(prog.name)}`); }}
                      >View →</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
